import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap'
import client from '../../api/client'

function Newsletter() {
  const [subject, setSubject] = useState('')
  const [contentSummary, setContentSummary] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [result, setResult] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [subscriberCount, setSubscriberCount] = useState(null)
  const [loadingCount, setLoadingCount] = useState(false)

  useEffect(() => {
    fetchSubscriberCount()
  }, [])

  const fetchSubscriberCount = async () => {
    setLoadingCount(true)
    try {
      const response = await client.get('/admin/api/v1/mailing/stats')
      setSubscriberCount(response.data.total_subscribers || 0)
    } catch (err) {
      console.error('Error fetching subscriber count:', err)
      setSubscriberCount(null)
    } finally {
      setLoadingCount(false)
    }
  }

  const handleSendClick = () => {
    if (!subject.trim()) {
      setError('Le sujet est requis')
      setTimeout(() => setError(''), 3000)
      return
    }
    if (!contentSummary.trim()) {
      setError('Le contenu est requis')
      setTimeout(() => setError(''), 3000)
      return
    }

    setShowConfirmModal(true)
  }

  const handleConfirmSend = async () => {
    setShowConfirmModal(false)
    setSending(true)
    setError('')
    setSuccess('')
    setResult(null)

    try {
      const response = await client.post('/admin/api/v1/mailing/newsletter/send', {
        subject: subject.trim(),
        content_summary: contentSummary.trim()
      })

      const data = response.data
      setResult(data)
      setSuccess(`Newsletter envoyée avec succès!`)
      setSubject('')
      setContentSummary('')
      fetchSubscriberCount()
    } catch (err) {
      console.error('Error sending newsletter:', err)
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi de la newsletter'
      setError(errorMsg)
    } finally {
      setSending(false)
    }
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📰 Newsletter</h2>
          <p className="text-muted">Envoyer une newsletter à tous les abonnés</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Composer la newsletter</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Sujet</Form.Label>
                  <Form.Control
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ex: Nouveautés de février 2026"
                    disabled={sending}
                    maxLength={200}
                  />
                  <Form.Text className="text-muted">
                    {subject.length}/200 caractères
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contenu</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={contentSummary}
                    onChange={(e) => setContentSummary(e.target.value)}
                    placeholder="Rédigez le contenu de votre newsletter ici. Ce texte sera inséré dans le template SendGrid configuré."
                    disabled={sending}
                  />
                  <Form.Text className="text-muted">
                    Le template SendGrid sera utilisé pour le formatage final. {contentSummary.length} caractères.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSendClick}
                    disabled={sending || !subject.trim() || !contentSummary.trim()}
                  >
                    {sending ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Envoi en cours...
                      </>
                    ) : (
                      '📤 Envoyer la newsletter'
                    )}
                  </Button>
                  {subject && contentSummary && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setSubject('')
                        setContentSummary('')
                        setResult(null)
                      }}
                      disabled={sending}
                    >
                      Réinitialiser
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>

          {result && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">📊 Résultats de l'envoi</h5>
              </Card.Header>
              <Card.Body>
                <Row className="text-center">
                  <Col md={4}>
                    <div className="mb-2">
                      <h3 className="text-success mb-0">{result.sent || 0}</h3>
                      <small className="text-muted">Envoyés</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-2">
                      <h3 className="text-danger mb-0">{result.failed || 0}</h3>
                      <small className="text-muted">Échecs</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-2">
                      <h3 className="text-primary mb-0">{result.total || 0}</h3>
                      <small className="text-muted">Total</small>
                    </div>
                  </Col>
                </Row>
                {result.status === 'sending' && (
                  <Alert variant="info" className="mt-3 mb-0">
                    <small>
                      <strong>ℹ️ Envoi en arrière-plan</strong> - L'envoi est en cours. Les emails seront envoyés progressivement.
                    </small>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">ℹ️ Informations</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Abonnés actifs</strong>
                <div className="mt-1">
                  {loadingCount ? (
                    <Spinner animation="border" size="sm" />
                  ) : subscriberCount !== null ? (
                    <Badge bg="primary" className="fs-6">{subscriberCount}</Badge>
                  ) : (
                    <Badge bg="secondary">N/A</Badge>
                  )}
                </div>
              </div>

              <hr />

              <div className="small text-muted">
                <p>
                  <strong>Template SendGrid</strong><br />
                  Le template configuré pour le type "newsletter" sera utilisé.
                  Assurez-vous qu'il est configuré et activé dans les Templates Email.
                </p>
                <p>
                  <strong>Variables disponibles</strong><br />
                  Le template reçoit : <code>subject</code>, <code>content_summary</code>, <code>app_name</code>, <code>frontend_url</code>, <code>support_email</code>
                </p>
                <p className="mb-0">
                  <strong>⚠️ Attention</strong><br />
                  L'envoi est irréversible. Vérifiez le contenu avant d'envoyer.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer l'envoi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Êtes-vous sûr de vouloir envoyer cette newsletter ?
          </p>
          {subscriberCount !== null && (
            <Alert variant="warning" className="mb-0">
              <strong>📧 {subscriberCount} abonné(s)</strong> recevront cet email.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleConfirmSend}>
            Confirmer l'envoi
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default Newsletter
